import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import dayjs from 'dayjs';
import { wizardBridge } from '../services/wizard-bridge';
import { patternRevealBridge } from '../services/pattern-reveal-bridge';
import { plannerTourBridge } from '../services/planner-tour-bridge';

/** Schwelle (Tage), ab der ein Muster-Start als „weit in der Zukunft" gilt —
 *  konsistent mit dem START_FAR_FUTURE-Hinweis im Wizard (Schritt 4). */
const FAR_FUTURE_DAYS = 30;

/** Tage bis zum gemerkten Startdatum der frisch angelegten Vorlage (>=0),
 *  oder null wenn keins gemerkt ist. */
const patternStartDaysAway = (): number | null => {
  const start = patternRevealBridge.getLastStart();
  if (!start) return null;
  return dayjs(start).startOf('day').diff(dayjs().startOf('day'), 'day');
};

/** Startet das eben angelegte Muster erst weit in der Zukunft? */
const patternStartsFarFuture = (): boolean => {
  const days = patternStartDaysAway();
  return days !== null && days > FAR_FUTURE_DAYS;
};

/**
 * Hat das Summary-Banner (Step 4) nicht-blockierende Hinweise? Liest die
 * Readiness-Ampel synchron ueber die Wizard-Bridge. `partial`/`blocked` =>
 * der Mentor schaltet auf die Best-Practice-Hinweis-Variante (Tooltip +
 * Voice-Over). `ok`/`null` => neutrale Standard-Variante. Wird in den
 * `content`/`audioStepId`-Closures von `a6-confirm` bei jedem Render
 * synchron aufgerufen — nur ein Bridge-Snapshot, kein Service-Lookup.
 */
const patternSummaryHasHints = (): boolean => {
  const state = wizardBridge.getReadinessState();
  return state === 'partial' || state === 'blocked';
};

/**
 * MENTOR — Zweig ▦ "Wochenplan direkt" / Wizard (Weg A · §9.2).
 *
 * Gestartet aus W1 (MentorEntry) oder aus der Wiederholungs-Bruecke (E.9) des
 * Einzelschicht-Zweigs. Fuehrt in den ▦-Muster-Modus und den 4-Schritt-Wizard
 * (StaffingWizardComponent), der ein Wochenmuster anlegt.
 *
 * Mode-Wechsel: `launchContext.route = '/planner?view=muster'` setzt den
 * ▦-Modus (wie die alte StaffingManagement-Tour), damit der Wochenmuster-Button
 * im DOM ist. Wizard-Mechanik 1:1 wiederverwendet aus der (in AP-0 stillgelegten)
 * staffing-management-Tour: `wizardBridge`, Dokument-Events
 * `tour:wizard-preset-chosen` / `-next-clicked` / `-back-clicked` /
 * `-submit-clicked`, `wizardStep`-Annotation + `dialogContext.waitForOpen`.
 *
 * AP-6 / 6c. Der Uebergang nach „Muster starten" in MentorReunite (R1) folgt
 * in 6e — bis dahin endet der Zweig mit einem PlannerView-Abschluss.
 */
export const MENTOR_PATTERN_TOUR: TourConfig = {
  id: TourFeature.MentorPattern,
  version: 2,
  title: 'TUTORIAL.MENTOR.PATTERN.TITLE',
  description: 'TUTORIAL.MENTOR.PATTERN.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/planner?view=muster' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: false,
  },
  priority: 210,
  estimatedDurationSec: 300,
  // feature-discovery = gefuehrter Flow (runTourV2), NICHT help-mode. Nicht im
  // Setup-Counter (zaehlt nur category==='setup').
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  // Setzt den ▦-Muster-Modus (Query-Param), damit der Wochenmuster-Button da ist.
  launchContext: { route: '/planner?view=muster' },
  chapters: [
    {
      id: 'pattern',
      title: 'TUTORIAL.MENTOR.PATTERN.TITLE',
      steps: [
        {
          // A.1 — Muster-Blick (wir sind via ?view=muster schon hier). Der
          // Text erklaert "Ich hab auf den ▦ Muster-Blick gewechselt" — also
          // spotlighten wir den 👤/▦-Umschalter selbst, damit der User SIEHT,
          // WO der Wechsel passiert ist (statt nur den ganzen Container).
          // Anchor existiert auch im Muster-Modus (gesetzt in
          // work-shifts-planner-timeline, gleicher Attach-Lauf wie der
          // WeekPatternsDropdown-Anker des Folgeschritts A.2). PlannerView
          // bleibt impliziter Fallback ueber die Anchor-Aufloesung.
          id: 'a1-overview',
          anchor: AnchorId.PlannerViewSwitch,
          spotlightAnchor: AnchorId.PlannerViewSwitch,
          title: 'TUTORIAL.MENTOR.PATTERN.A1.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A1.CONTENT',
          placement: 'bottom',
          showNextButton: true,
        },
        {
          // A.2 — Direkter „＋ Neues Muster"-Button (D-21, modus-adaptiver
          // ＋-Button im ▦-Modus = `.fc-addShiftButton-button`, Anker
          // `add-shift-button`). EIN Klick öffnet den Wizard via
          // `openNewPatternWizard()`. Wir spotlighten zusätzlich das
          // ▦ Wochenmuster-Dropdown, weil derselbe Befehl auch dort steckt —
          // der Nutzer sieht beide Wege, der direkte ist der empfohlene.
          id: 'a2-new-pattern',
          anchor: AnchorId.AddShiftButton,
          spotlightAnchor: AnchorId.AddShiftButton,
          additionalSpotlightAnchors: [AnchorId.WeekPatternsDropdown],
          title: 'TUTORIAL.MENTOR.PATTERN.A2.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A2.CONTENT',
          placement: 'bottom',
          // Egal welcher Pfad (Direktbutton ODER ▦-Menü → „＋ Neues Muster"):
          // NICHT auf den Klick selbst advancen, sondern erst wenn der
          // Wizard-Dialog offen+gerendert ist (Event aus StaffingWizard.ngAfterViewInit).
          advanceOnDocumentEvent: 'tour:wizard-opened',
          showNextButton: false,
        },
        {
          // A.3 — Wizard Schritt 1: Was für eine Schicht? (Vorlage oder manuell)
          id: 'a3-what',
          anchor: AnchorId.WizardPresetSection,
          spotlightAnchor: AnchorId.WizardContent,
          additionalSpotlightAnchors: [AnchorId.WizardPresetSkipButton],
          title: 'TUTORIAL.MENTOR.PATTERN.A3.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A3.CONTENT',
          placement: 'right',
          advanceOnDocumentEvent: 'tour:wizard-preset-chosen',
          showNextButton: false,
          dialogContext: {
            attachToDialog: true,
            waitForOpen: true,
            popoverOutsideDialog: true,
          },
          wizardStep: 1,
          onBeforeShow: () => wizardBridge.goToStep(1),
        },
        {
          // A.3b — Grundinfo (Name + Abteilung), noch Wizard-Schritt 1.
          id: 'a3-basics',
          anchor: AnchorId.WizardBasicsSection,
          spotlightAnchor: AnchorId.WizardBasicsSection,
          additionalSpotlightAnchors: [AnchorId.WizardNextButton],
          title: 'TUTORIAL.MENTOR.PATTERN.A3B.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A3B.CONTENT',
          placement: 'right',
          advanceOnDocumentEvent: 'tour:wizard-next-clicked',
          showNextButton: false,
          dialogContext: { attachToDialog: true, popoverOutsideDialog: true },
          wizardStep: 1,
        },
        {
          // A.4 — Wizard Schritt 2: Wann + Wiederholung (der Hebel).
          id: 'a4-when',
          anchor: AnchorId.WizardRecurrenceHeader,
          spotlightAnchor: AnchorId.WizardRecurrenceBlock,
          // Zeit-Section zusätzlich ins Spotlight, damit Start-/Endzeit beim
          // Erklären sichtbar (nicht abgedunkelt) sind — der Voice-Over-Cue
          // pulst auf den Zeitfeldern, während er sie nennt (siehe Cues
          // `staffing-wizard-time-inputs` / `-recurrence-header` im Manifest).
          additionalSpotlightAnchors: [
            AnchorId.WizardTimeBlock,
            AnchorId.WizardNextButton,
          ],
          title: 'TUTORIAL.MENTOR.PATTERN.A4.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A4.CONTENT',
          placement: 'right',
          advanceOnDocumentEvent: 'tour:wizard-next-clicked',
          backOnDocumentEvent: 'tour:wizard-back-clicked',
          // Kein Mentor-„Weiter": die Tour folgt NUR dem Wizard-eigenen Weiter
          // (Event), sonst kann der Nutzer Tour ≠ Wizard entkoppeln (Derail).
          showNextButton: false,
          dialogContext: { attachToDialog: true, popoverOutsideDialog: true },
          wizardStep: 2,
        },
        {
          // A.5 — Wizard Schritt 3: Wie viele Leute? (= Soll der Ampel)
          id: 'a5-howmany',
          // XDS-Quick-Win-Welle 2026-08-07: die separate `.section-header`-Zeile
          // (`WizardStaffSection`) entfiel bei der Migration auf `xds-section` — der
          // Titel wird jetzt intern von der Komponente gerendert, ohne eigenes
          // adressierbares Anker-Element. `anchor` zeigt deshalb wie `spotlightAnchor`
          // auf den ganzen Block (identisches Muster bereits bei `a3-basics` oben).
          anchor: AnchorId.WizardStaffBlock,
          spotlightAnchor: AnchorId.WizardStaffBlock,
          additionalSpotlightAnchors: [AnchorId.WizardNextButton],
          title: 'TUTORIAL.MENTOR.PATTERN.A5.TITLE',
          content: 'TUTORIAL.MENTOR.PATTERN.A5.CONTENT',
          placement: 'right',
          advanceOnDocumentEvent: 'tour:wizard-next-clicked',
          backOnDocumentEvent: 'tour:wizard-back-clicked',
          // Kein Mentor-„Weiter": die Tour folgt NUR dem Wizard-eigenen Weiter
          // (Event), sonst kann der Nutzer Tour ≠ Wizard entkoppeln (Derail).
          showNextButton: false,
          dialogContext: { attachToDialog: true, popoverOutsideDialog: true },
          wizardStep: 3,
        },
        {
          // A.6 — Wizard Schritt 4: Passt das? → „Muster starten".
          id: 'a6-confirm',
          anchor: AnchorId.WizardSummarySection,
          spotlightAnchor: AnchorId.WizardSummaryBlock,
          // Readiness-/Hinweis-Banner mit ins Spotlight (un-dimmt), damit der
          // Bereich „oben mit den Hinweisen" sichtbar beleuchtet ist, während
          // der Mentor darauf eingeht — analog zur Zeit-Section in a4-when.
          additionalSpotlightAnchors: [
            AnchorId.WizardReadiness,
            AnchorId.WizardSubmitButton,
          ],
          title: 'TUTORIAL.MENTOR.PATTERN.A6.TITLE',
          // Adaptiv: stehen oben Readiness-Hinweise, gibt der Mentor einen
          // Best-Practice-Tipp dazu (Tooltip + Voice-Over). Sonst neutral.
          content: () =>
            patternSummaryHasHints()
              ? 'TUTORIAL.MENTOR.PATTERN.A6.CONTENT_WARN'
              : 'TUTORIAL.MENTOR.PATTERN.A6.CONTENT',
          // Voice-Over folgt synchron demselben Zweig: `a6-confirm-warn`
          // (Best-Practice) bzw. die neutrale `a6-confirm`-Spur.
          audioStepId: () =>
            patternSummaryHasHints() ? 'a6-confirm-warn' : 'a6-confirm',
          placement: 'right',
          advanceOnDocumentEvent: 'tour:wizard-submit-clicked',
          backOnDocumentEvent: 'tour:wizard-back-clicked',
          showNextButton: false,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
            autoCloseOnDialogClose: true,
          },
          wizardStep: 4,
        },
        {
          // A.7 — Abschluss (6c): Muster läuft. R1-Wiedervereinigung folgt in 6e.
          id: 'a7-done',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.PATTERN.DONE.TITLE',
          // Adaptiv: startet das Muster erst weit in der Zukunft, erklaert der
          // Mentor das konkret (Datum + Tage) und springt die Planung zur
          // Startwoche — sonst der neutrale „läuft"-Text.
          content: () =>
            patternStartsFarFuture()
              ? 'TUTORIAL.MENTOR.PATTERN.DONE.CONTENT_FUTURE'
              : 'TUTORIAL.MENTOR.PATTERN.DONE.CONTENT',
          contentParams: () => {
            const start = patternRevealBridge.getLastStart();
            return {
              date: start ? dayjs(start).format('DD.MM.YYYY') : '',
              days: patternStartDaysAway() ?? 0,
            };
          },
          // Voice-Over folgt: generische Zukunfts-Spur (kann das konkrete
          // Datum nicht sprechen — das steht im Tooltip) bzw. neutrale Spur.
          audioStepId: () =>
            patternStartsFarFuture() ? 'a7-done-future' : 'a7-done',
          // Bei Zukunfts-Start: Planung zur Startwoche springen, damit die
          // erzeugten Schichten sichtbar sind statt einer leeren Woche.
          onBeforeShow: () => {
            if (patternStartsFarFuture()) {
              const start = patternRevealBridge.getLastStart();
              if (start) {
                plannerTourBridge.goToDate(dayjs(start).format('YYYY-MM-DD'));
              }
            }
          },
          placement: 'auto',
          showNextButton: false,
          showPrevButton: false,
          scrollDelay: 400,
          // → Endstrang (R1-Ernte-Reveal „die App hat's gemacht" ist jetzt
          // dessen erster Schritt — kein separater MentorReunite-Tour mehr).
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.PATTERN.DONE.CTA',
              icon: 'fa-solid fa-arrow-right',
              startTour: TourFeature.MentorEndgame,
              variant: 'primary',
            },
          ],
        },
      ],
    },
  ],
};
